// client/src/components/MerchantFid.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import MerchantNavbar from './MerchantNavbar';
import { FaArrowLeft } from 'react-icons/fa';

import {
  getAuth,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  getDocFromCache,
  query,
  where,
} from 'firebase/firestore';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { firestore, storage } from '../../firebase';

/* =======================================================
   Utils
   ======================================================= */
const raf = () => new Promise((r) => requestAnimationFrame(r));

/* =======================================================
   Logo commerçant avec cache localStorage (TTL 24h)
   → N'affecte PAS l'affichage des cartes (seulement la perf)
   ======================================================= */
const LOGO_TTL_MS = 24 * 60 * 60 * 1000;

const MerchantLogo = ({ merchantId }) => {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    if (!merchantId) return;
    const cacheKey = `merchantLogo_${merchantId}`;

    // 1) Affiche le cache immédiatement si présent
    const cachedRaw = localStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const parsed = JSON.parse(cachedRaw);
        if (parsed?.url) setLogoUrl(parsed.url);
      } catch {}
    }

    let mounted = true;

    // 2) Rafraîchit si TTL expiré ou pas de cache
    (async () => {
      try {
        let cached = null;
        try { cached = cachedRaw ? JSON.parse(cachedRaw) : null; } catch {}
        const freshEnough =
          cached && typeof cached.ts === 'number' && Date.now() - cached.ts < LOGO_TTL_MS;

        if (freshEnough) {
          // Cache récent → on évite une balade Storage
          return;
        }

        const listRef = ref(storage, `${merchantId}/photo-profil/`);
        const { items } = await listAll(listRef);
        if (!items.length) {
          localStorage.removeItem(cacheKey);
          if (mounted) setLogoUrl(null);
          return;
        }

        const withMeta = await Promise.all(
          items.map(async (item) => {
            const { timeCreated } = await getMetadata(item);
            return { item, ts: timeCreated ? new Date(timeCreated).getTime() : 0 };
          })
        );
        withMeta.sort((a, b) => b.ts - a.ts);
        const { item, ts } = withMeta[0];

        const freshUrl = await getDownloadURL(item);
        localStorage.setItem(cacheKey, JSON.stringify({ url: freshUrl, ts: ts || Date.now() }));
        if (mounted) setLogoUrl(freshUrl);
      } catch (err) {
        // En cas d’erreur, on garde le cache affiché s’il existe
        console.error('Erreur logo commerçant:', err);
      }
    })();

    return () => { mounted = false; };
  }, [merchantId]);

  return logoUrl ? (
    <img
      src={logoUrl}
      alt="Logo du commerçant"
      className="fid-card-logo"
      style={{
        width: 56,
        height: 56,
        display: 'block',
        objectFit: 'cover',
        aspectRatio: '1 / 1',
        borderRadius: '50%',
        border: '1px solid var(--eb-border)',
        flexShrink: 0,
      }}
      loading="lazy"
    />
  ) : null;
};

/* =======================================================
   MerchantFid — même logique de récupération/affichage
   Optimisation légère: 1 seul listener root + abonnements à l’ajout
   ======================================================= */
const MerchantFid = () => {
  const navigate = useNavigate();

  const [fidelityData, setFidelityData] = useState([]);
  const [authLoaded, setAuthLoaded] = useState(false);

  // Hover UX (inchangé)
  const [canHover, setCanHover] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mqHoverFine = window.matchMedia('(hover: hover) and (pointer: fine)');
    const mqDesktop = window.matchMedia('(min-width: 769px)');

    const evaluate = () => setCanHover(Boolean(mqHoverFine.matches && mqDesktop.matches));

    evaluate();
    const add = (m, fn) => (m.addEventListener ? m.addEventListener('change', fn) : m.addListener(fn));
    const remove = (m, fn) => (m.removeEventListener ? m.removeEventListener('change', fn) : m.removeListener(fn));

    add(mqHoverFine, evaluate);
    add(mqDesktop, evaluate);
    window.addEventListener('orientationchange', evaluate);

    return () => {
      remove(mqHoverFine, evaluate);
      remove(mqDesktop, evaluate);
      window.removeEventListener('orientationchange', evaluate);
    };
  }, []);

  // state normalisé des cartes (clé = merchantId)
  const dataMap = useRef({});
  // cache méta marchand (évite re-lectures)
  const merchantMetaCache = useRef({}); // { [merchantId]: { Type, multiple, gift, points1, pseudo } }
  const pendingMetaFetch = useRef(new Set());
  // listeners legacy/doc par marchand
  const legacyListeners = useRef({}); // { [merchantId]: unsubscribe }
  const perMerchantListListeners = useRef({}); // { [merchantId]: unsubscribe }

  // Batch setState pour ne pas rerender N fois d’affilée
  const flushing = useRef(false);
  const flushState = useCallback(async () => {
    if (flushing.current) return;
    flushing.current = true;
    await raf();
    flushing.current = false;

    // Tri stable (pseudo puis id) pour un rendu cohérent
    const arr = Object.values(dataMap.current).sort((a, b) => {
      const pa = (a.merchantPseudo || '').toLowerCase();
      const pb = (b.merchantPseudo || '').toLowerCase();
      if (pa < pb) return -1;
      if (pa > pb) return 1;
      return (a.merchantId || '').localeCompare(b.merchantId || '');
    });
    setFidelityData(arr);
  }, []);

  // Persistance auth
  useEffect(() => {
    setAuthLoaded(true);
  }, []);

  // Charge méta marchand (tentative cache Firestore d’abord, SANS filtrer)
  const loadMerchantMeta = useCallback(async (merchantId) => {
    if (merchantMetaCache.current[merchantId] || pendingMetaFetch.current.has(merchantId)) return;
    pendingMetaFetch.current.add(merchantId);
    try {
      const fidRef = doc(firestore, 'fidelity', merchantId);
      const userRef = doc(firestore, 'users', merchantId);

      let mSnap = null;
      let uSnap = null;
      try { mSnap = await getDocFromCache(fidRef); } catch {}
      try { uSnap = await getDocFromCache(userRef); } catch {}
      if (!mSnap) mSnap = await getDoc(fidRef).catch(() => null);
      if (!uSnap) uSnap = await getDoc(userRef).catch(() => null);

      merchantMetaCache.current[merchantId] = {
        Type: mSnap?.exists() ? (mSnap.data().Type || 'Fidbycount') : 'Fidbycount',
        multiple: mSnap?.exists() ? (mSnap.data().multiple || 0) : 0,
        gift: mSnap?.exists() ? (mSnap.data().gift || '') : '',
        points1: mSnap?.exists() ? (mSnap.data().points1 || 0) : 0,
        pseudo: uSnap?.exists() ? (uSnap.data().pseudo || 'Inconnu') : 'Inconnu',
      };
    } catch (e) {
      console.warn('Meta marchand indisponible:', e);
      merchantMetaCache.current[merchantId] = {
        Type: 'Fidbycount', multiple: 0, gift: '', points1: 0, pseudo: 'Inconnu',
      };
    } finally {
      pendingMetaFetch.current.delete(merchantId);
    }
  }, []);

  // Prépare/MAJ une carte à partir des méta + données client
  const upsertCard = useCallback(async (merchantId, clientData) => {
    await loadMerchantMeta(merchantId);
    const meta = merchantMetaCache.current[merchantId] || {};
    dataMap.current[merchantId] = {
      merchantId,
      merchantPseudo: meta.pseudo || 'Inconnu',
      multiple: meta.multiple || 0,
      compteur: clientData?.compteur ?? 0,
      gift: meta.gift || '',
      code: clientData?.code,
      Type: meta.Type || 'Fidbycount',
      points: clientData?.points ?? 0,
      firstRewardPoints: meta.points1 || 0,
    };
    flushState();
  }, [loadMerchantMeta, flushState]);

  // Écoutes: 1) schéma "liste par marchand" filtrée par clientUid
  //          2) fallback legacy si doc {uid} existe (sans 2e listener root)
  useEffect(() => {
    if (!authLoaded) return;
    const auth = getAuth();

    let stopClientUid = null;

    const stopAllPerMerchant = () => {
      Object.values(perMerchantListListeners.current).forEach(fn => fn && fn());
      perMerchantListListeners.current = {};
    };
    const stopAllLegacy = () => {
      Object.values(legacyListeners.current).forEach(fn => fn && fn());
      legacyListeners.current = {};
    };

    const stopAuth = onAuthStateChanged(auth, async (user) => {
      // cleanup complet
      if (stopClientUid) { stopClientUid(); stopClientUid = null; }
      stopAllPerMerchant();
      stopAllLegacy();
      dataMap.current = {};
      setFidelityData([]);

      if (!user) return;
      const uid = user.uid;

      // (1) Écoute 'fidelity' une seule fois (root)
      stopClientUid = onSnapshot(
        collection(firestore, 'fidelity'),
        async (merchantsSnap) => {
          const changes = merchantsSnap.docChanges();

          await Promise.all(
            changes.map(async (change) => {
              const merchantId = change.doc.id;

              if (change.type === 'removed') {
                // Nettoyage des listeners de ce marchand
                if (perMerchantListListeners.current[merchantId]) {
                  perMerchantListListeners.current[merchantId]();
                  delete perMerchantListListeners.current[merchantId];
                }
                if (legacyListeners.current[merchantId]) {
                  legacyListeners.current[merchantId]();
                  delete legacyListeners.current[merchantId];
                }
                // Supprime la carte si plus aucune source n’alimente
                delete dataMap.current[merchantId];
                flushState();
                return;
              }

              // IMPORTANT: on ne (ré)abonne que quand le marchand est ajouté
              if (change.type === 'added') {
                // Listener ciblé list/{clientUid}
                if (perMerchantListListeners.current[merchantId]) {
                  perMerchantListListeners.current[merchantId](); // reset si existant
                  delete perMerchantListListeners.current[merchantId];
                }
                const listRef = collection(firestore, 'fidelity', merchantId, 'list');
                const qList = query(listRef, where('clientUid', '==', uid));
                perMerchantListListeners.current[merchantId] = onSnapshot(
                  qList,
                  (listSnap) => {
                    if (listSnap.empty) {
                      // Supprime seulement si le legacy ne couvre pas ce marchand
                      const hasLegacy = !!legacyListeners.current[merchantId];
                      if (!hasLegacy) {
                        delete dataMap.current[merchantId];
                        flushState();
                      }
                      return;
                    }
                    // s’il y a plusieurs docs (doublons), on prend le premier
                    const cData = listSnap.docs[0].data() || {};
                    upsertCard(merchantId, cData);
                  },
                  (err) => console.error('per-merchant list listener error:', err)
                );

                // Fallback legacy: on n’ouvre un listener que si le doc {uid} existe
                const legacyRef = doc(firestore, 'fidelity', merchantId, 'list', uid);
                const legacySnap = await getDoc(legacyRef).catch(() => null);
                if (legacySnap?.exists() && !legacyListeners.current[merchantId]) {
                  legacyListeners.current[merchantId] = onSnapshot(
                    legacyRef,
                    (snap) => {
                      if (snap.exists()) {
                        upsertCard(merchantId, snap.data());
                      } else {
                        if (legacyListeners.current[merchantId]) {
                          legacyListeners.current[merchantId]();
                          delete legacyListeners.current[merchantId];
                        }
                        const hasPerMerchant = !!perMerchantListListeners.current[merchantId];
                        if (!hasPerMerchant) {
                          delete dataMap.current[merchantId];
                          flushState();
                        }
                      }
                    },
                    (err) => console.error('legacy doc listener error:', err)
                  );
                }
              }

              // Sur 'modified' au root: pas de re-subscription (économie)
              // Les mises à jour utiles arriveront via les listeners déjà en place.
            })
          );
        },
        (err) => console.error('fidelity root listener error:', err)
      );
    });

    return () => {
      if (stopClientUid) stopClientUid();
      Object.values(legacyListeners.current).forEach(fn => fn && fn());
      legacyListeners.current = {};
      Object.values(perMerchantListListeners.current).forEach(fn => fn && fn());
      perMerchantListListeners.current = {};
      if (stopAuth) stopAuth();
    };
  }, [authLoaded, flushState, upsertCard]);

  const handleBackClick = () => navigate('/merchantdocs');
  const handleFidelityClick = (merchantId) => navigate(`/merchantfidviewer/${merchantId}`);
  const goToTransfer = () => navigate('/transfertfid'); // ← bouton partage (route marchande)

  const eligibleStyles = (active) => ({
    background: active ? 'var(--eb-warning-bg)' : 'var(--eb-surface-2)',
    border: `1px solid ${active ? '#fde3b0' : 'var(--eb-border)'}`,
  });

  return (
    <div className="dashboard-container" data-hover={canHover ? '1' : '0'}>
      <MerchantNavbar />

      <div className="mf-scope">
        {/* En-tête */}
        <button onClick={handleBackClick} className="eb-btn eb-btn--ghost eb-btn--sm mf-back">
          <FaArrowLeft /> Retour
        </button>
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Fidélité</h1>
            <p className="eb-page-subtitle">Vos cartes de fidélité chez les commerçants</p>
          </div>
          <button onClick={goToTransfer} className="eb-btn eb-btn--secondary">
            Partager des points
          </button>
        </header>

        {/* Cartes fidélité */}
        {fidelityData.length > 0 ? (
          <div className="mf-grid">
            {fidelityData.map((item) => {
              const eligibleCount = item.Type === 'Fidbycount' && item.multiple - item.compteur === 1;
              const eligiblePoints = item.Type === 'Fidbypoints' && item.points >= item.firstRewardPoints;

              return (
                <div
                  key={item.merchantId}
                  className="mf-card"
                  onClick={() => handleFidelityClick(item.merchantId)}
                  role="button"
                  tabIndex={0}
                >
                  {/* header visuel */}
                  <div className="fid-card-header mf-card-header">
                    <MerchantLogo merchantId={item.merchantId} />
                    <h2 className="fid-card-title mf-card-title">
                      {item.merchantPseudo}
                    </h2>
                  </div>

                  {item.Type === 'Fidbycount' ? (
                    <>
                      <p className="mf-progress">
                        {item.compteur} / {item.multiple} passages
                      </p>

                      {item.gift && (
                        <div className="mf-reward" style={eligibleStyles(eligibleCount)}>
                          <p className="mf-reward-label" style={{ color: eligibleCount ? 'var(--eb-warning)' : 'var(--eb-muted)' }}>
                            {eligibleCount ? 'Gain au prochain passage :' : 'Gain verrouillé :'}
                          </p>
                          <p className="mf-reward-gift">
                            {item.gift}
                          </p>
                          {item.code && (
                            <p className="mf-reward-code">code : {item.code}</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="mf-reward" style={eligibleStyles(eligiblePoints)}>
                      <p className="mf-reward-gift" style={{ marginTop: 0, fontWeight: 700 }}>
                        Vos points : {item.points} pts
                      </p>
                      <p className="mf-reward-code">
                        {eligiblePoints ? 'Cliquez pour voir les récompenses' : `À ${item.firstRewardPoints} pts pour récompense`}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mf-empty">
            Aucun commerçant dans votre liste de fidélité pour le moment.
          </div>
        )}
      </div>

      <style>{`
        .mf-scope { width: 100%; max-width: var(--eb-content-max); min-width: 0; }
        .mf-scope .eb-page-title { margin: 0; text-align: left; }
        .mf-scope .mf-back { margin-bottom: 12px; padding-left: 8px; }
        .mf-scope .mf-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 16px; }
        .mf-scope .mf-card { min-width: 0; padding: 20px; text-align: left; cursor: pointer; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); transition: box-shadow .15s ease, border-color .15s ease; }
        .mf-scope .mf-card:hover { border-color: var(--eb-border-strong); box-shadow: var(--eb-shadow-md); }
        .mf-scope .mf-card:focus-visible { outline: none; box-shadow: var(--eb-ring); }
        .mf-scope .mf-card-header { display: flex; align-items: center; gap: 12px; padding-bottom: 14px; margin-bottom: 14px; border-bottom: 1px solid var(--eb-border); min-width: 0; }
        .mf-scope .mf-card-title { margin: 0; font-size: 16px; font-weight: 650; color: var(--eb-text); text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
        .mf-scope .mf-progress { margin: 0 0 12px; font-size: 14px; color: var(--eb-text-2); font-weight: 500; }
        .mf-scope .mf-reward { padding: 12px 14px; border-radius: var(--eb-radius); min-width: 0; }
        .mf-scope .mf-reward-label { margin: 0; font-size: 13px; font-weight: 600; }
        .mf-scope .mf-reward-gift { margin: 6px 0 0; font-size: 14.5px; color: var(--eb-text); overflow-wrap: anywhere; }
        .mf-scope .mf-reward-code { margin: 6px 0 0; font-size: 13px; color: var(--eb-muted); }
        .mf-scope .mf-empty { padding: 40px 24px; text-align: center; color: var(--eb-muted); font-size: 14px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); }
        @media (max-width: 768px) {
          .mf-scope .mf-grid { grid-template-columns: 1fr; gap: 12px; }
          .mf-scope .mf-card { padding: 16px; }
        }
        @media (max-width: 600px) {
          .mf-scope .eb-page-header > .eb-btn { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default MerchantFid;
